import {useMutation} from '@github-ui/react-query'
import {createIssueFromChecklistItemMutation as createIssueFromChecklistItemQueryId} from '@github-ui/server-persisted-queries'
import type {
  CreateIssueFromChecklistItemMutationMutation,
  CreateIssueFromChecklistItemMutationMutationVariables,
} from '@github-ui/server-persisted-queries/types/createIssueFromChecklistItemMutation'
import {commitMutation, graphql} from 'react-relay'
import type {Environment} from 'relay-runtime'

import type {
  createIssueFromChecklistItemMutation,
  createIssueFromChecklistItemMutation$data,
  CreateIssueInput,
} from './__generated__/createIssueFromChecklistItemMutation.graphql'
import type {ChecklistItemConversionRoute} from './checklist-item-conversion-helpers'
import {
  installIssueBodyInRelay,
  postChecklistItemConversion,
  settleIssueFields,
  throwPayloadErrors,
} from './checklist-item-conversion-helpers'

type MutationProps = {
  environment: Environment
  input: CreateIssueInput
  onError?: (error: Error) => void
  onCompleted?: (respnse: createIssueFromChecklistItemMutation$data) => void
}
export function commitCreateIssueFromChecklistItemMutation({
  environment,
  input: {title, repositoryId, parentIssueId, position},
  onError,
  onCompleted,
}: MutationProps) {
  const inputHash: CreateIssueInput = {
    title,
    repositoryId,
    parentIssueId,
    position,
  }

  return commitMutation<createIssueFromChecklistItemMutation>(environment, {
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    mutation: graphql`
      mutation createIssueFromChecklistItemMutation($input: CreateIssueInput!) @raw_response_type {
        createIssue(input: $input) {
          issue {
            parent {
              body
              bodyHTML(unfurlReferences: true)
            }
          }
          errors {
            message
          }
        }
      }
    `,
    variables: {
      input: inputHash,
    },
    onError: error => onError && onError(error),
    onCompleted: response => onCompleted && onCompleted(response),
  })
}

type SourceIssue = NonNullable<
  NonNullable<
    NonNullable<CreateIssueFromChecklistItemMutationMutation['createIssue']>['issue']
  >['repository']['sourceIssue']
>

export type CreateIssueFromChecklistItemVariables = ChecklistItemConversionRoute & {
  title: CreateIssueFromChecklistItemMutationMutationVariables['input']['title']
  position: readonly number[]
}

function sourceIssueFromResponse(data: CreateIssueFromChecklistItemMutationMutation): SourceIssue {
  const payload = data.createIssue
  if (!payload) throw new Error('createIssue returned a null payload')
  throwPayloadErrors(payload.errors, 'createIssue')
  if (!payload.issue) throw new Error('createIssue returned a null issue')

  const sourceIssue = payload.issue.repository.sourceIssue
  if (!sourceIssue) throw new Error('createIssue returned a null source issue')
  return sourceIssue
}

export function useCreateIssueFromChecklistItemMutation(environment: Environment) {
  return useMutation<CreateIssueFromChecklistItemMutationMutation, Error, CreateIssueFromChecklistItemVariables>({
    mutationFn: async ({owner, repository, issueNumber, title, position}) => {
      const input: Pick<CreateIssueFromChecklistItemMutationMutationVariables['input'], 'title' | 'position'> = {
        title,
        position: [...position],
      }
      const data = await postChecklistItemConversion<CreateIssueFromChecklistItemMutationMutation, typeof input>(
        {owner, repository, issueNumber},
        'checklist_item_issues',
        input,
        createIssueFromChecklistItemQueryId,
      )
      sourceIssueFromResponse(data)
      return data
    },
    onSuccess: async data => {
      const sourceIssue = sourceIssueFromResponse(data)
      const operation = {
        type: 'setFields' as const,
        id: sourceIssue.id,
        fields: {body: sourceIssue.body, bodyHTML: sourceIssue.bodyHTML, bodyVersion: sourceIssue.bodyVersion},
      }
      installIssueBodyInRelay(environment, sourceIssue)
      await settleIssueFields(operation)
    },
  })
}
